/* eslint-disable no-param-reassign */
/**
 * This file contains mapper functions to map response objects which are returned
 * from Contentful API by Netlify functions written in `/netlify/functions`.
 * Mappers are responsible for mapping each response object to the shape of objects
 * returned from GatsbyContentfulSource plugin & Gatsby GraphQL fragments.
 */

import {
    BLOG_TERM_AUTHOR,
    BLOG_TERM_INDUSTRIES,
    BLOG_TERM_TOPICS,
    BLOG_TERM_TRENDS,
    blogCategoryFieldMapping,
    BLOG_BUTTON_TEXT,
    BLOG_BUTTON_TYPE,
} from "@qlik/qlik/src/lib/constants/blog-constants";
import {
    NEWS_TERM_NEWS_INDUSTRY,
    NEWS_TERM_NEWS_TYPE,
    newsCategoryFieldMapping,
    NEWS_BUTTON_TEXT,
    NEWS_BUTTON_TYPE,
} from "@qlik/qlik/src/lib/constants/news-constants";
import {
    RESOURCE_TERM_INDUSTRIES,
    RESOURCE_TERM_PRODUCT_INTERESTS,
    RESOURCE_TERM_RESOURCE_TYPE,
    RESOURCE_TERM_TOPIC,
    resourceCategoryFieldMapping,
} from "@qlik/qlik/src/lib/constants/resource-constants";
import { BLOG_BUTTON_KEY } from "@qlik/qlik/src/lib/constants/microcopy-constants";
import { TYPE_CTA, contentTypeContentfulTypeMap } from "@qlik/qlik/src/lib/constants/contentful-types";
import { getMicrocopyValueFromKeys } from "@qlik/qlik/src/lib/util/helpers";

/**
 * mapCategoryItem transform a category item from the Contentful API response into a structured format suitable for application use
 * If the @categoryFilter is falsy, it returns null.
 * Otherwise, it returns an object with properties id, title, subTerms, and isDisabled extracted from the categoryFilter
 * If the categoryFilter has subTerms, it maps each subTerm using the same mapCategoryItem function
 */
const mapCategoryItem = (categoryFilter) => {
    if (!categoryFilter) return null;
    return {
        id: categoryFilter.sys?.id,
        title: categoryFilter.fields?.title,
        subTerms: categoryFilter.fields?.subTerms ? categoryFilter.fields?.subTerms?.map(mapCategoryItem) : null,
        isDisabled: categoryFilter.isDisabled ?? false,
    };
};

/**
 * mediaItemMapper maps a single Media item Response to the supported format
 * If the @mediaItem is falsy, it returns null
 * Otherwise, it returns an object with the __typename set to "ContentfulMedia", the fields from the mediaItem, and the id from the mediaItem's sys property
 */
const mediaItemMapper = (mediaItem) => {
    if (!mediaItem) return null;

    return { __typename: "ContentfulMedia", ...mediaItem.fields, id: mediaItem?.sys?.id };
};

/**
 * authorMapper maps a Contentful author item to a structured format
 * It takes an authorItem object as input and returns an object with properties firstName, lastName, and avatar extracted from the @authorItem
 * The avatar property is mapped using the mediaItemMapper function
 */
const authorMapper = (authorItem) => ({
    firstName: authorItem?.fields?.firstName,
    lastName: authorItem?.fields?.lastName,
    avatar: mediaItemMapper(authorItem?.fields?.avatar),
});

/**
 * categoriesMapper maps a list of category items to the supported format
 * It takes an array of @categoryItems as input and returns an array of mapped category items
 * Each category item is mapped using the mapCategoryItem function, and falsy values are filtered out
 */
const categoriesMapper = (categoryItems) => categoryItems?.map(mapCategoryItem).filter(Boolean);

/**
 * mediaMapper maps a list of Media items to the supported format
 * It takes an array of @mediaList as input and returns an array of mapped media items
 * Each media item is mapped using the mediaItemMapper function, and falsy values are filtered out
 */
const mediaMapper = (mediaList) => mediaList?.map(mediaItemMapper).filter(Boolean);

/**
 * ctaMapper maps a list of conteful CTA items to the supported format
 * It takes an array of @ctaList as input. The function first removes duplicates from the list
 * It then returns an array of mapped CTA items. Each CTA item is mapped to an object with its fields and id
 * If the CTA item has an internalLink or icon field, these are also included in the mapped object
 */
const ctaMapper = (ctaList) => {
    ctaList = Array.from(new Set(ctaList?.map(JSON.stringify))).map(JSON.parse);
    return ctaList?.map((cta) => {
        const mapped = {
            ...cta.fields,
            id: cta?.sys?.id,
        };

        if (cta?.fields?.internalLink) {
            const internalLink = cta?.fields?.internalLink;
            mapped.internalLink = {
                ...internalLink?.fields,
                __typename: contentTypeContentfulTypeMap[internalLink?.sys?.contentType?.sys?.id],
            };
        }

        if (cta?.fields?.icon) {
            mapped.icon = cta?.fields?.icon?.map((icon) => ({ ...icon?.fields, id: icon?.sys?.id }));
        }

        return mapped;
    });
};

/**
 * microCopyMapper maps a Conteful Microcopy group to the supported format
 * It takes a @microcopyGroup object as input. If the microcopyGroup is falsy, it returns null
 * Otherwise, it returns an object with the fields from the microcopyGroup and a mapped microcopy array
 * Each item in the microcopy array is mapped to an object with its fields, a value object, and id
 */
const microCopyMapper = (microcopyGroup) => {
    if (!microcopyGroup) return null;

    return {
        ...microcopyGroup?.fields,
        microcopy: microcopyGroup?.fields?.microcopy?.map((item) => ({
            ...item.fields,
            value: { value: item.fields.value },
            id: item?.sys?.id,
        })),
    };
};

/**
 * cardItemsMapper maps the Contentful CardItems items to the supported format
 * It takes an array of @cardItems as input and returns an array of mapped CardItems
 * Each card item is mapped to an object that includes its fields,
 * a __typename derived from a mapping based on its system content type ID,
 * and its media items mapped using the mediaMapper function.
 */
const cardItemsMapper = (cardItems) =>
    cardItems?.map((cardItem) => ({
        ...cardItem.fields,
        __typename: contentTypeContentfulTypeMap[cardItem?.sys?.contentType?.sys?.id],
        media: mediaMapper(cardItem?.media),
    }));

/**
 * componentMapper maps the Contentful Components to the supported format
 * It takes an array of @componentsList as input and returns an array of mapped Components
 * Each component is mapped to an object that includes its fields
 * If the component has cardItems, they are mapped using the cardItemsMapper function
 */
const componentMapper = (componentsList) =>
    componentsList?.map((component) => {
        const mapped = {
            ...component.fields,
        };

        if (component.fields?.cardItems) {
            mapped.cardItems = cardItemsMapper(component.fields?.cardItems);
        }
        return mapped;
    });

/**
 * resourceMapper transform a @resourceDataList of Contentful resource data into a format that is compatible and easily consumable by the application's front-end components
 * This transformation process involves re-structuring the raw data from Contentful into a more organized and accessible format, ensuring that each resource is ready for display or further processing within the application's UI
 */
const resourceMapper = (resourceDataList) =>
    resourceDataList?.map((resource) => ({
        __typename: "ContentfulResource",
        ...resource.fields,
        components: componentMapper(resource.fields.components),
        [resourceCategoryFieldMapping[RESOURCE_TERM_TOPIC]]: categoriesMapper(
            resource.fields?.[resourceCategoryFieldMapping[RESOURCE_TERM_TOPIC]]
        ),
        [resourceCategoryFieldMapping[RESOURCE_TERM_INDUSTRIES]]: categoriesMapper(
            resource.fields?.[resourceCategoryFieldMapping[RESOURCE_TERM_INDUSTRIES]]
        ),
        [resourceCategoryFieldMapping[RESOURCE_TERM_PRODUCT_INTERESTS]]: categoriesMapper(
            resource.fields?.[resourceCategoryFieldMapping[RESOURCE_TERM_PRODUCT_INTERESTS]]
        ),
        [resourceCategoryFieldMapping[RESOURCE_TERM_RESOURCE_TYPE]]: categoriesMapper(
            resource.fields?.[resourceCategoryFieldMapping[RESOURCE_TERM_RESOURCE_TYPE]]
        ),
        featuredImage: mediaItemMapper(resource.fields?.featuredImage),
        image: mediaItemMapper(resource.fields?.image),
        cta: ctaMapper(resource.fields?.cta),
        microcopyGroup: microCopyMapper(resource.fields?.microcopyGroup),
    }));

/**
 * newsDefaultCta generates a default Call to Action (CTA) for Contentful news items
 * It takes a @slug as input and returns an array containing a single CTA object
 * The CTA object includes the __typename, name, text, internalLink, and type properties, configured for news items
 */
const newsDefaultCta = (slug) => {
    return [
        {
            __typename: TYPE_CTA,
            name: NEWS_BUTTON_TEXT,
            text: NEWS_BUTTON_TEXT,
            internalLink: {
                slug: `news/${slug}`,
            },
            type: NEWS_BUTTON_TYPE,
        },
    ];
};

/**
 * newsMapper maps the Contentful news data list to the shape supported by components
 * It takes an array of @newsDataList as input and returns an array of mapped news data
 * Each news item is mapped to an object that includes its fields, category mappings for NEWS_TERM_NEWS_TYPE and NEWS_TERM_NEWS_INDUSTRY using mapCategoryItem and categoriesMapper respectively
 * It also maps featuredImage and image using the mediaItemMapper function, and cta using either the ctaMapper function or the newsDefaultCta function if no cta is provided
 */
const newsMapper = (newsDataList) =>
    newsDataList?.map((news) => ({
        __typename: "ContentfulNews",
        ...news.fields,
        [newsCategoryFieldMapping[NEWS_TERM_NEWS_TYPE]]: mapCategoryItem(
            news.fields?.[newsCategoryFieldMapping[NEWS_TERM_NEWS_TYPE]]
        ), // Single reference
        [newsCategoryFieldMapping[NEWS_TERM_NEWS_INDUSTRY]]: categoriesMapper(
            news.fields?.[newsCategoryFieldMapping[NEWS_TERM_NEWS_INDUSTRY]]
        ),
        featuredImage: mediaItemMapper(news.fields?.featuredImage),
        image: mediaItemMapper(news.fields?.image),
        cta: news.fields?.cta ? ctaMapper(news.fields?.cta) : newsDefaultCta(news.fields?.slug),
    }));

/**
 * blogMapper maps the Contentful blog data list according to the fragment structure
 * It takes an array of @blogDataList as input and returns an array of mapped blog data
 * Each blog item is processed to include its fields, microcopyGroup, and category mappings for topics, industries, and trends using the respective mapper functions
 * It also maps the featuredImage, image, and author using their specific mapper functions
 * The CTA for each blog item is constructed using either a custom button text obtained from the microcopyGroup or a default text, along with the blog's slug and a predefined button type
 */
const blogMapper = (blogDataList) => {
    return blogDataList?.map((blog) => {
        const microcopyGroup = microCopyMapper(blog.fields?.microcopyGroup);
        const buttonText = getMicrocopyValueFromKeys(microcopyGroup, [BLOG_BUTTON_KEY])?.[0] || null;

        return {
            __typename: "ContentfulBlog",
            id: blog?.sys?.id,
            ...blog.fields,
            microcopyGroup,
            [blogCategoryFieldMapping[BLOG_TERM_TOPICS]]: categoriesMapper(
                blog.fields?.[blogCategoryFieldMapping[BLOG_TERM_TOPICS]]
            ),
            [blogCategoryFieldMapping[BLOG_TERM_INDUSTRIES]]: categoriesMapper(
                blog.fields?.[blogCategoryFieldMapping[BLOG_TERM_INDUSTRIES]]
            ),
            [blogCategoryFieldMapping[BLOG_TERM_TRENDS]]: categoriesMapper(
                blog.fields?.[blogCategoryFieldMapping[BLOG_TERM_TRENDS]]
            ),
            featuredImage: mediaItemMapper(blog.fields?.featuredImage),
            image: mediaItemMapper(blog.fields?.image),
            [blogCategoryFieldMapping[BLOG_TERM_AUTHOR]]: authorMapper(
                blog.fields?.[blogCategoryFieldMapping[BLOG_TERM_AUTHOR]]
            ),
            cta: [
                {
                    __typename: TYPE_CTA,
                    name: buttonText || BLOG_BUTTON_TEXT,
                    text: buttonText || BLOG_BUTTON_TEXT,
                    internalLink: {
                        slug: `blog/${blog.fields?.slug}`,
                    },
                    type: BLOG_BUTTON_TYPE,
                },
            ],
        };
    });
};

export { mapCategoryItem, categoriesMapper, resourceMapper, newsMapper, blogMapper, authorMapper, mediaMapper };
